import { DiamondShapes } from "@/types/types";
import { computed, reactive, readonly, watch } from "vue";
import JSONAPI from "@mediamonks/jsonapi-client";
import { RingBand } from "@/api/RingBand";
import { Diamond } from "@/api/Diamond";
import { clarityValuesMap, colorValuesMap } from "@/utils/diamondComparison";

interface State {
    variantId: number | null;
    availableDiamonds: Array<Diamond>;
    caratMin: number;
    caratMax: number;
    priceMin: number;
    priceMax: number;
    userSelection: {
        shape: DiamondShapes | null;
        caratMin: number;
        caratMax: number;
        diamond: Diamond | null;
        priceMin: number;
        priceMax: number;
        clarity: string | null;
    };
    infoSelectedDiamond: Diamond | null;
    showInfoModal: boolean;
}

const state = reactive({
    variantId: null,
    availableDiamonds: [],
    caratMin: 0,
    caratMax: 2,
    priceMin: 0,
    priceMax: 100000,
    userSelection: {
        shape: null,
        caratMin: 0,
        caratMax: 0,
        diamond: null,
        priceMin: 0,
        priceMax: 0,
        clarity: null,
    },
    infoSelectedDiamond: null,
    showInfoModal: false,
} as State);

function setVariantId(variantId: number) {
    state.variantId = variantId;
}

function setAvailableDiamonds(availableDiamonds: Array<Diamond>) {
    state.availableDiamonds = availableDiamonds;
}

function setSelectedShape(shape: DiamondShapes) {
    state.userSelection.shape = shape;
}

function setCaratMin(caratMin: number) {
    state.userSelection.caratMin = caratMin;
}

function setCaratMax(caratMax: number) {
    state.userSelection.caratMax = caratMax;
}

function setDiamond(diamond: Diamond) {
    state.userSelection.diamond = diamond;

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("dS", diamond.id);
    window.history.replaceState(
        {},
        "",
        window.location.pathname + "?" + urlParams.toString()
    );
}

function setClarity(value: string | null) {
    state.userSelection.clarity = value;
}

function setShowInfoModal(value: boolean) {
    state.showInfoModal = value;
}

function setInfoSelectedDiamond(diamond: Diamond) {
    state.infoSelectedDiamond = diamond;
}

function setPriceMin(priceMin: number) {
    state.userSelection.priceMin = priceMin;
}

function setPriceMax(priceMax: number) {
    state.userSelection.priceMax = priceMax;
}

const filteredDiamonds = computed(() => {
    let filtered = state.availableDiamonds;

    filtered = filtered.filter(
        (diamond) =>
            diamond.carat >= state.userSelection.caratMin &&
            diamond.carat <= state.userSelection.caratMax
    );

    filtered = filtered.filter(
        (diamond) =>
            diamond.price >= state.userSelection.priceMin &&
            diamond.price <= state.userSelection.priceMax
    );

    if (state.userSelection.shape !== null) {
        filtered = filtered.filter(
            (diamond) => diamond.shape === state.userSelection.shape
        );
    }

    if (state.userSelection.clarity !== null) {
        filtered = filtered.filter(
            (diamond) =>
                clarityValuesMap[
                    diamond.clarity as keyof typeof clarityValuesMap
                ] >=
                clarityValuesMap[
                    state.userSelection.clarity as keyof typeof clarityValuesMap
                ]
        );
    }

    return filtered;
});

const comparableDiamonds = computed(() => {
    if (state.userSelection.diamond === null) {
        return {
            moreCarat: null,
            betterColor: null,
            higherClarity: null,
        };
    }

    const allowedPriceDelta = 1000 * 100;
    const allowedCaratDelta = 0.2;

    const id = state.userSelection.diamond.id;
    const shape = state.userSelection.diamond.shape;
    const carat = state.userSelection.diamond.carat;
    const color = state.userSelection.diamond.color;
    const clarity = state.userSelection.diamond.clarity;
    const price = state.userSelection.diamond.price;

    const moreCarat =
        state.availableDiamonds
            .filter((d) => d.id !== id)
            .filter((d) => d.shape === shape)
            .filter((d) => Math.abs(price - d.price) < allowedPriceDelta)
            .filter((d) => d.carat > carat)
            .sort((a, b) => a.price - b.price)?.[0] ?? null;

    const betterColor =
        state.availableDiamonds
            .filter((d) => d.id !== id)
            .filter((d) => d.shape === shape)
            .filter((d) => Math.abs(carat - d.carat) < allowedCaratDelta)
            .filter((d) => Math.abs(price - d.price) < allowedPriceDelta)
            .filter(
                (d) =>
                    colorValuesMap[d.color as keyof typeof colorValuesMap] >
                    colorValuesMap[color as keyof typeof colorValuesMap]
            )
            .sort((a, b) => a.price - b.price)?.[0] ?? null;

    const higherClarity =
        state.availableDiamonds
            .filter((d) => d.id !== id)
            .filter((d) => d.shape === shape)
            .filter((d) => Math.abs(carat - d.carat) < allowedCaratDelta)
            .filter((d) => Math.abs(price - d.price) < allowedPriceDelta)
            .filter(
                (d) =>
                    clarityValuesMap[
                        d.clarity as keyof typeof clarityValuesMap
                    ] >
                    clarityValuesMap[clarity as keyof typeof clarityValuesMap]
            )
            .sort((a, b) => a.price - b.price)?.[0] ?? null;

    return {
        moreCarat,
        betterColor,
        higherClarity,
    };
});

const selectedDiamond = computed(() => state.userSelection.diamond);

const selectedVariant = computed(() => state.variantId);

const caratMin = computed(() => state.caratMin);

const caratMax = computed(() => state.caratMax);

const priceMin = computed(() => state.priceMin);

const priceMax = computed(() => state.priceMax);

function handleDiamondResponse(diamonds: Diamond[]) {
    state.availableDiamonds = diamonds;
    state.userSelection.diamond = null;

    const caratMax = Math.max.apply(
        null,
        diamonds.map((diamond) => diamond.carat)
    );

    state.userSelection.caratMin = 0;
    state.caratMax = caratMax;
    state.userSelection.caratMax = caratMax;

    const priceMax = Math.max.apply(
        null,
        diamonds.map((diamond) => diamond.price)
    );

    state.userSelection.priceMin = 0;
    state.priceMax = priceMax;
    state.userSelection.priceMax = priceMax;
}

async function fetchDiamonds(variantId: number | null) {
    const url = new URL(process.env.VUE_APP_API_ENDPOINT);
    const client = JSONAPI.client(url);

    const endpoint = client.endpoint(RingBand);

    if (variantId !== null) {
        try {
            const result = await endpoint.getToManyRelationship(
                variantId.toString(),
                "diamonds",
                { "filter[singleSaleOnly]": "true" }
            );

            handleDiamondResponse(result.data);
        } catch (e) {
            handleDiamondResponse([]);
        }
    } else {
        const diamondEndpoint = client.endpoint(Diamond);
        const result = await diamondEndpoint.getMany();

        handleDiamondResponse(result.data);
    }
}

function initializeStore() {
    const variantIdInputV2 = document.querySelector("form > input[name=id]") as
        | HTMLInputElement
        | HTMLSelectElement
        | null;

    if (variantIdInputV2 !== null) {
        state.variantId = Number.parseInt(variantIdInputV2.value);
    }

    document.addEventListener(
        "change-variant",
        ((event: CustomEvent) =>
            (state.variantId = event.detail.id)) as EventListener
    );

    fetchDiamonds(selectedVariant.value);

    watch(selectedVariant, fetchDiamonds);
}

watch(state, () => console.log({ ...state }));

export default {
    initializeStore,
    state: readonly(state),
    computed: {
        filteredDiamonds,
        selectedDiamond,
        caratMin,
        caratMax,
        priceMin,
        priceMax,
        comparableDiamonds,
    },
    actions: {
        setVariantId,
        setAvailableDiamonds,
        setSelectedShape,
        setCaratMin,
        setCaratMax,
        setDiamond,
        setShowInfoModal,
        setInfoSelectedDiamond,
        setPriceMin,
        setPriceMax,
        setClarity,
    },
};
